import React, { useContext, useState, useEffect } from 'react';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import { AddIcon, DeleteIcon, EditIconPen } from 'assests/GmsIcons';
import LeftButton, { RightButton } from 'Components/GMS/Rules/Components/LeftButton';
import FilterModal from './FilterModal';
import { noSpinnerStyles } from '../../utils/styles';

type ConditionProps = {
    conditionId: string;
    position: number;
    isMainCondition: boolean;
};

const Condition: React.FC<ConditionProps> = ({ conditionId, position, isMainCondition }) => {
    const {
        iterationPayload,
        activeIterationId,
        updateCondition,
        removeCondition,
        indentCondition,
        attributeList,
        addCondition,
        flattenConditions
    } = useContext<IterationContextType>(IterationContext);

    const currentIteration = iterationPayload?.iterations?.find(i => i.id === activeIterationId);

    // Find the operation condition in the flattened structure
    const operationConditions = currentIteration ?
        flattenConditions(currentIteration.conditions) : [];

    const condition = operationConditions.find(c => c.id === conditionId);

    const [attributeId, setAttributeId] = useState<string>("");
    const [baseAttributeId, setBaseAttributeId] = useState<string>("");
    const [operator, setOperator] = useState<string>("==");
    const [valueType, setValueType] = useState<string>("Absolute");
    const [arithmeticOp, setArithmeticOp] = useState<string>("");
    const [operandValue, setOperandValue] = useState<string>('');
    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

    useEffect(() => {
        if (condition) {
            setAttributeId(condition.attribute_id || "");
            setBaseAttributeId(condition.attribute_id || "");
            setOperator(condition.condition || "==");
            setValueType(condition.value_type || "Absolute");
            setArithmeticOp(condition.operator || "");
            setOperandValue(condition.operand?.toString() || '');
        }
    }, [condition]);

    const handleAttributeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newAttributeId = e.target.value;
        setAttributeId(newAttributeId);

        const attribute = attributeList.find(attr => attr.value === newAttributeId);

        if (activeIterationId) {
            updateCondition(activeIterationId, conditionId, {
                attribute_id: newAttributeId,
                attribute_name: attribute?.label || ""
            });
        }
    };

    const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newOperator = e.target.value;
        setOperator(newOperator);

        if (activeIterationId) {
            updateCondition(activeIterationId, conditionId, {
                condition: newOperator
            });
        }
    };

    const handleValueTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValueType = e.target.value;
        setValueType(newValueType);

        let newPrefix = "";
        let newSuffix = "";
        let newOperator = "";
        let newOperand = "";

        if (newValueType === "Multiplier") {
            newPrefix = "X";
            setArithmeticOp("");
            setOperandValue("1");
            newOperand = "1";
        } else if (newValueType === "Percentage") {
            newSuffix = "%";
            setArithmeticOp("");
            setOperandValue("");
        } else {
            setArithmeticOp("");
            setOperandValue("");
        }

        if (activeIterationId) {
            updateCondition(activeIterationId, conditionId, {
                value_type: newValueType,
                operand: newOperand ? Number(newOperand) : 0,
                prefix: newPrefix,
                suffix: newSuffix,
                operator: newOperator
            });
        }
    };

    const handleBaseAttributeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newBaseAttributeId = e.target.value;
        setBaseAttributeId(newBaseAttributeId);
    };

    const handleArithmeticOpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newArithmeticOp = e.target.value;
        setArithmeticOp(newArithmeticOp);

        // Set default operand value based on value type when arithmetic operator is selected
        let defaultOperand = "";
        if (newArithmeticOp && valueType !== "Multiplier") {
            if (valueType === "Percentage") {
                defaultOperand = "10";
            } else {
                defaultOperand = "0";
            }
            setOperandValue(defaultOperand);
        }

        if (activeIterationId) {
            updateCondition(activeIterationId, conditionId, {
                operator: newArithmeticOp,
                operand: defaultOperand ? Number(defaultOperand) : 0
            });
        }
    };

    const handleOperandChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setOperandValue(inputValue);

        if (inputValue.trim() === '') {
            if (activeIterationId) {
                updateCondition(activeIterationId, conditionId, {
                    operand: 0
                });
            }
            return;
        }

        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
            if (activeIterationId) {
                updateCondition(activeIterationId, conditionId, {
                    operand: numericValue
                });
            }
        }
    };

    const handleDelete = () => {
        if (activeIterationId) {
            removeCondition(activeIterationId, conditionId);
        }
    };

    const handleAddNestedCondition = () => {
        if (activeIterationId) {
            addCondition(activeIterationId, conditionId);
        }
    };

    const handleOpenFilterModal = (e: React.MouseEvent) => {
        e.preventDefault();
        setShowFilterModal(true);
    };

    const handleCloseFilterModal = () => {
        setShowFilterModal(false);
    };

    const standardSelectStyle = {
        fontSize: ".8rem",
        color: "#172B4D",
        fontWeight: "400",
        height: "36px",
        borderRadius: "4px",
        border: "1px solid #DFE1E6",
        backgroundColor: "white",
        padding: "0 32px 0 8px",
        textOverflow: "ellipsis"
    };

    const filterCount = condition?.filters?.length || 0;

    return (
        <div className={`tw-bg-[#FAFAFC] tw-py-2 ${isMainCondition ? "tw-border tw-border-[#E4E4E8] " : ''}`}>
            <div className="iteration-sub-condition">
                <div className="left-parenthesis" />
                <div className="right-indent">
                    <RightButton onClick={() => indentCondition(activeIterationId || '', conditionId, 'right')} />
                    <div className="tw-py-1 tw-bg-white"></div>
                    <LeftButton onClick={() => indentCondition(activeIterationId || '', conditionId, 'left')} />
                </div>

                <div className="tw-w-full tw-ms-2" style={{ gridRow: "1/2", gridColumn: "4/2" }}>
                    <div className="tw-flex tw-justify-between tw-items-start tw-gap-1">
                        {/* Left side - Selected filters */}
                        <div className="tw-flex-1 tw-min-w-0">
                            {condition?.filters && condition.filters.length > 0 && (
                                <div className="tw-space-y-1">
                                    {condition.filters.map((filter, index) => (
                                        <div key={filter.id}>
                                            <div className="tw-text-blue-950 tw-text-sm tw-font-semibold tw-leading-tight tw-break-words">
                                                {filter.attribute_name} {filter.comparison_rule} {filter.attribute_value}
                                            </div>
                                            {index < condition.filters.length - 1 && (
                                                <div className="tw-text-gray-400 tw-text-xs tw-font-semibold tw-leading-tight tw-mt-1 tw-mb-1">
                                                    {filter.logical_operator || 'AND'}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right side - Filter button */}
                        <div className="tw-flex-shrink-0">
                            <button
                                className="add-logic-filter-button tw-flex tw-items-center"
                                onClick={handleOpenFilterModal}
                                data-testid={`open-filter-modal-${conditionId}`}
                            >
                                {filterCount > 0 ? <EditIconPen /> : "+"}
                                <span>&nbsp;Filter</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="left-attributes tw-w-full tw-overflow-visible" style={{ gridRow: "2/3", gridColumn: "3/12" }}>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-w-full tw-justify-between tw-pt-2">
                        <select
                            id={`attribute-select-${conditionId}`}
                            name={`attribute-${conditionId}`}
                            className="form-select drop-down-dm tw-ps-2 tw-flex-1 tw-min-w-[180px] tw-max-w-[280px] tw-pr-8"
                            value={attributeId}
                            onChange={handleAttributeChange}
                            title={attributeId ? attributeList.find(attr => attr.value === attributeId)?.label || "Select Attribute" : "Select Attribute"}
                            style={{
                                ...standardSelectStyle
                            }}
                        >
                            <option value="">Select Attribute</option>
                            {attributeList.map(attr => (
                                <option key={attr.value} value={attr.value}>
                                    {attr.label}
                                </option>
                            ))}
                        </select>

                        <select
                            id={`operator-select-${conditionId}`}
                            name={`operator-${conditionId}`}
                            className="form-select drop-down-dm tw-flex-shrink-0"
                            value={operator}
                            onChange={handleOperatorChange}
                            style={{
                                ...standardSelectStyle,
                                width: "100px",
                                textAlign: "start",
                                fontWeight: "600"
                            }}
                        >
                            <option value=">">{">"}</option>
                            <option value="<">{"<"}</option>
                            <option value=">=">{">="}</option>
                            <option value="<=">{"<="}</option>
                            <option value="==">==</option>
                            <option value="!=">!=</option>
                        </select>

                        <select
                            id={`value-type-select-${conditionId}`}
                            name={`valueType-${conditionId}`}
                            className="form-select drop-down-dm tw-flex-shrink-0"
                            value={valueType}
                            onChange={handleValueTypeChange}
                            style={{
                                ...standardSelectStyle,
                                width: "128px"
                            }}
                        >
                            <option value="Absolute">Absolute</option>
                            <option value="Multiplier">Multiplier</option>
                            <option value="Percentage">Percentage</option>
                        </select>

                        <select
                            id={`base-attribute-select-${conditionId}`}
                            name={`baseAttribute-${conditionId}`}
                            className="form-select drop-down-dm tw-flex-1 tw-min-w-[180px] tw-max-w-[280px] tw-pr-8"
                            value={baseAttributeId}
                            onChange={handleBaseAttributeChange}
                            title={attributeId ? attributeList.find(attr => attr.value === attributeId)?.label || "Select Attribute" : "Select Attribute"}
                            style={{
                                ...standardSelectStyle
                            }}
                        >
                            <option value="">Base Attribute</option>
                            {attributeList.map(attr => (
                                <option key={attr.value} value={attr.value}>
                                    {attr.label}
                                </option>
                            ))}
                        </select>

                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                            {valueType !== "Multiplier" ? (
                                <select
                                    id={`arithmetic-op-select-${conditionId}`}
                                    name={`arithmeticOp-${conditionId}`}
                                    className="form-select drop-down-dm"
                                    value={arithmeticOp}
                                    onChange={handleArithmeticOpChange}
                                    style={{
                                        ...standardSelectStyle,
                                        width: "80px",
                                        fontSize: "1.1rem"
                                    }}
                                >
                                    <option value="" disabled>+/-</option>
                                    <option value="+">+</option>
                                    <option value="-">-</option>
                                </select>
                            ) : (
                                <div className="tw-w-[80px]"></div>
                            )}

                            {valueType === "Multiplier" && <span className="tw-w-5 tw-text-center tw-ms-2">
                                {valueType === "Multiplier" && "x"}
                            </span>}

                            <div className="tw-flex tw-items-center">
                                <style>{noSpinnerStyles}</style>
                                {!(valueType !== "Multiplier" && !arithmeticOp) ? <input
                                    id={`operand-input-${conditionId}`}
                                    name={`operand-${conditionId}`}
                                    type="number"
                                    min="1"
                                    inputMode="decimal"
                                    className="form-control"
                                    value={operandValue}
                                    onChange={handleOperandChange}
                                    disabled={valueType !== "Multiplier" && !arithmeticOp}
                                    placeholder={""}
                                    style={{
                                        ...standardSelectStyle,
                                        width: "60px",
                                        backgroundColor: (valueType !== "Multiplier" && !arithmeticOp) ? "#f5f5f5" : "white",
                                        cursor: (valueType !== "Multiplier" && !arithmeticOp) ? "not-allowed" : "text"
                                    }}
                                /> : <div className="tw-w-[60px]"></div>}
                                {valueType === "Percentage" && <span className="tw-w-5 tw-text-center tw-ms-2">
                                    {valueType === "Percentage" && arithmeticOp && "%"}
                                </span>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="center-compatible-text" style={{ visibility: "hidden" }}></div>
                <div className="right-attributes" style={{ visibility: "hidden" }}></div>
                <div className="right-closing-parenthesis" />

                <div className="mt-1 delete-btn-cba d-flex align-items-center">
                    <div>
                        <button
                            className="delete-button"
                            onClick={handleDelete}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                </div>
            </div>

            {isMainCondition && (
                <button
                    className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[#0747A6] tw-rounded-md hover:tw-bg-blue-50"
                    onClick={handleAddNestedCondition}
                >
                    <AddIcon color='#0747A6' />
                    <span className="tw-text-[#0747A6] tw-text-sm">Add Condition</span>
                </button>
            )}

            <FilterModal
                isOpen={showFilterModal}
                conditionId={conditionId}
                onClose={handleCloseFilterModal}
            />
        </div>
    );
};

export default Condition;